import { useVersionCheck } from "@medicorepay/shared/lib/use-version-check";
import type { EffectiveAccessContext } from "@medicorepay/shared";
import { VersionBanner } from "@medicorepay/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { X } from "lucide-react";
import { ToastBar, Toaster, toast } from "react-hot-toast";
import { BrowserRouter } from "react-router-dom";
import { useEffect } from "react";
import { PWAInstallPrompt } from "./components/pwa/PWAInstallPrompt";
import { OnlineOnlyBoundary } from "./components/offline/OnlineOnlyBoundary";
import { TelemetryBootstrap } from "./components/TelemetryBootstrap";
import { useForceLogoutListener } from "./hooks/useForceLogoutListener";
import AppRoutes from "./routes";
import {
  syncExternalAuthStorage,
  useAuthStore,
  type User,
} from "./stores/authStore";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  useForceLogoutListener();

  useEffect(() => {
    const handleSessionRefresh = (event: Event) => {
      const response = (
        event as CustomEvent<{
          user?: User;
          accessToken?: string;
          refreshToken?: string;
        }>
      ).detail;
      if (response?.user) {
        useAuthStore
          .getState()
          .replaceSessionUser(
            response.user,
            response.accessToken,
            response.refreshToken,
          );
      }
    };
    const handleAccessContext = (event: Event) => {
      const context = (event as CustomEvent<EffectiveAccessContext>).detail;
      if (context) {
        useAuthStore.getState().applyAccessContext(context);
      }
    };
    const handleExternalSession = (event: StorageEvent) => {
      if (event.key === "auth-storage") {
        syncExternalAuthStorage(event.newValue);
      }
    };
    window.addEventListener("auth-session-refreshed", handleSessionRefresh);
    window.addEventListener("access-context-updated", handleAccessContext);
    window.addEventListener("storage", handleExternalSession);
    return () => {
      window.removeEventListener(
        "auth-session-refreshed",
        handleSessionRefresh,
      );
      window.removeEventListener("access-context-updated", handleAccessContext);
      window.removeEventListener("storage", handleExternalSession);
    };
  }, []);

  const versionCheck = useVersionCheck({
    apiBaseUrl: import.meta.env.VITE_API_URL || "http://localhost:5001",
    clientVersion: import.meta.env.VITE_APP_VERSION || "1.0.0",
    platform: "web",
    warnOnly: true,
  });

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <VersionBanner
          status={versionCheck.status}
          message={versionCheck.message}
          clientVersion={versionCheck.clientVersion}
          apiVersion={versionCheck.apiVersion}
          onDismiss={() => {}}
        />
        <TelemetryBootstrap />
        <Toaster
          position="top-center"
          reverseOrder={false}
          gutter={8}
          toastOptions={{
            duration: 5000,
            className: "",
            style: {
              background: "#fff",
              color: "#363636",
              padding: "16px",
              boxShadow:
                "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
              borderRadius: "12px",
              fontSize: "14px",
              fontWeight: 500,
              maxWidth: "400px",
              border: "1px solid #f3f4f6",
            },
            success: {
              iconTheme: {
                primary: "#10b981",
                secondary: "white",
              },
              style: {
                borderLeft: "4px solid #10b981",
              },
            },
            error: {
              iconTheme: {
                primary: "#ef4444",
                secondary: "white",
              },
              style: {
                borderLeft: "4px solid #ef4444",
              },
            },
          }}
        >
          {(t: any) => (
            <ToastBar toast={t}>
              {({
                icon,
                message,
              }: {
                icon: React.ReactNode;
                message: React.ReactNode;
              }) => (
                <div className="flex items-start w-full gap-2">
                  {icon}
                  <div className="flex-1 text-sm font-medium">{message}</div>
                  {t.type !== "loading" && (
                    <button
                      onClick={() => toast.dismiss(t.id)}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded-full hover:bg-gray-100"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </ToastBar>
          )}
        </Toaster>
        <OnlineOnlyBoundary>
          <AppRoutes />
          <PWAInstallPrompt />
        </OnlineOnlyBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
